'use client';
import { useState, useEffect } from 'react';
import { useTranslations } from '@/utils/translations';

interface ConsentState {
  analytics: boolean;
  advertising: boolean;
  personalization: boolean;
}

const CONSENT_KEY = 'cookie_consent_v2';

const defaultDenied: ConsentState = {
  analytics: false,
  advertising: false,
  personalization: false,
};

function pushConsentUpdate(consent: ConsentState) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  gtag('consent', 'update', {
    ad_storage: consent.advertising ? 'granted' : 'denied',
    ad_user_data: consent.advertising ? 'granted' : 'denied',
    ad_personalization: consent.personalization ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
  });
  window.dataLayer.push({
    event: 'consent_update',
    consent_analytics: consent.analytics ? 'granted' : 'denied',
    consent_advertising: consent.advertising ? 'granted' : 'denied',
    consent_personalization: consent.personalization ? 'granted' : 'denied',
  });
}

export default function CookieConsentModal() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<ConsentState>(defaultDenied);
  const { t } = useTranslations();

  useEffect(() => {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (saved) {
      try {
        const parsed: ConsentState = JSON.parse(saved);
        pushConsentUpdate(parsed);
        setVisible(false);
      } catch {
        setVisible(true);
      }
    } else {
      setVisible(true);
    }
  }, []);

  const saveConsent = (state: ConsentState) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
    pushConsentUpdate(state);
    setVisible(false);
  };

  const handleAcceptAll = () => {
    const all: ConsentState = { analytics: true, advertising: true, personalization: true };
    setConsent(all);
    saveConsent(all);
  };

  const handleRejectAll = () => {
    const none: ConsentState = { analytics: false, advertising: false, personalization: false };
    setConsent(none);
    saveConsent(none);
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('cookie_consent.modal_aria_label')}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-footer-background border border-[#4a3f6b] rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-[#4a3f6b]/50">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-lg font-semibold text-white">{t('cookie_consent.title')}</h2>
          </div>
          <p className="text-sm text-white leading-relaxed">
            {t('cookie_consent.description')}
          </p>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="px-6 py-4 border-b border-[#4a3f6b]/50 space-y-4">
            {/* Necessary - always on */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{t('cookie_consent.necessary.label')}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t('cookie_consent.necessary.description')}</p>
              </div>
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-11 h-6 bg-[#77C0BB] rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-60">
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{t('cookie_consent.analytics.label')}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t('cookie_consent.analytics.description')}</p>
              </div>
              <button
                role="switch"
                aria-checked={consent.analytics}
                onClick={() => setConsent(prev => ({ ...prev, analytics: !prev.analytics }))}
                className={`flex-shrink-0 mt-0.5 w-11 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                  consent.analytics ? 'bg-[#77C0BB] justify-end' : 'bg-gray-600 justify-start'
                }`}
              >
                <div className="w-4 h-4 bg-white rounded-full shadow" />
              </button>
            </div>

            {/* Advertising */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{t('cookie_consent.advertising.label')}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t('cookie_consent.advertising.description')}</p>
              </div>
              <button
                role="switch"
                aria-checked={consent.advertising}
                onClick={() => setConsent(prev => ({ ...prev, advertising: !prev.advertising }))}
                className={`flex-shrink-0 mt-0.5 w-11 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                  consent.advertising ? 'bg-[#77C0BB] justify-end' : 'bg-gray-600 justify-start'
                }`}
              >
                <div className="w-4 h-4 bg-white rounded-full shadow" />
              </button>
            </div>

            {/* Personalization */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{t('cookie_consent.personalization.label')}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t('cookie_consent.personalization.description')}</p>
              </div>
              <button
                role="switch"
                aria-checked={consent.personalization}
                onClick={() => setConsent(prev => ({ ...prev, personalization: !prev.personalization }))}
                className={`flex-shrink-0 mt-0.5 w-11 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                  consent.personalization ? 'bg-[#77C0BB] justify-end' : 'bg-gray-600 justify-start'
                }`}
              >
                <div className="w-4 h-4 bg-white rounded-full shadow" />
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-5">
          {showSettings ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-3 py-1.5 bg-[#77C0BB] hover:bg-[#5aada8] text-[#1a1a2e] font-semibold text-sm rounded-full transition-colors duration-200"
              >
                {t('cookie_consent.button.save')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-3 py-1.5 bg-[#77C0BB] hover:bg-[#5aada8] text-[#1a1a2e] font-semibold text-sm rounded-full transition-colors duration-200"
              >
                {t('cookie_consent.button.accept_all')}
              </button>
              <button
                onClick={handleRejectAll}
                className="flex-1 px-3 py-1.5 bg-[#2d2d4e] hover:bg-[#3d3d5e] text-gray-200 font-medium text-sm rounded-full border border-[#4a3f6b] transition-colors duration-200"
              >
                {t('cookie_consent.button.reject_all')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-3 py-1.55 bg-[#77C0BB] text-black hover:bg-[#5aada8] font-semibold text-sm rounded-full transition-colors duration-200"
              >
                {t('cookie_consent.button.accept_all')}
              </button>
              <button
                onClick={handleRejectAll}
                className="flex-1 px-3 py-1.5 bg-[#2d2d4e] hover:bg-[#3d3d5e] text-gray-200 font-medium text-sm rounded-full border border-[#4a3f6b] transition-colors duration-200"
              >
                {t('cookie_consent.button.reject_all')}
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="flex-1 px-3 py-1.5 bg-[#2d2d4e] hover:bg-[#3d3d5e] text-gray-200 font-medium text-sm rounded-full border border-[#4a3f6b] transition-colors duration-200"
              >
                {t('cookie_consent.button.settings')}
              </button>
            </div>
          )}
          <p className="text-xs text-white text-center mt-3">
            {t('cookie_consent.footer_note')}
          </p>
        </div>
      </div>
    </div>
  );
}
